import { EnumEntityReferenceType, NoteManyQuery } from "gql/generated";
import { z } from "zod";
import { useNotesContext } from "shared/contextProviders";
import { useFormHandler } from "shared/hooks";
import { useCallback, useMemo } from "react";
import i18n, { i18nNamespace, loadLanguagePacks } from "i18n";
import { format } from "date-fns";
import { DATE_PATTERN_SIMPLE } from "shared/utils";
import { toZonedTime } from "date-fns-tz";

// Ensure the required language packs are loaded
await loadLanguagePacks([i18nNamespace.Core]);

export const noteFormSchema = z.object({
  message: z
    .string({
      required_error: i18n.t("core:NOTES.ERROR.MESSAGE"),
    })
    .trim()
    .min(1, {
      message: i18n.t("core:NOTES.ERROR.MESSAGE"),
    }),
  mentions: z.array(
    z.object({
      id: z.string(),
      display: z.string(),
    }),
  ),
  noteDateISO: z.string().optional(),
  entity: z
    .object({
      id: z.string(),
      type: z.nativeEnum(EnumEntityReferenceType),
    })
    .optional(),
});

export type NoteFormValues = z.infer<typeof noteFormSchema>;

type UseNoteCardFormProps = {
  note?: NoteManyQuery["noteMany"][number];
  onSuccess: () => void;
  onError: () => void;
};

export const useNoteCardForm = ({
  note,
  onSuccess,
  onError,
}: UseNoteCardFormProps) => {
  const { defaultNoteEntity } = useNotesContext();
  const defaultValues: NoteFormValues = useMemo(
    () => ({
      message: note?.message ?? "",
      mentions:
        note?.mentions?.map(mention => ({
          id: mention._id,
          display: `${mention.firstName} ${mention.lastName}`,
        })) ?? [],
      noteDateISO: note?.noteDateISO
        ? format(toZonedTime(note.noteDateISO, "UTC"), DATE_PATTERN_SIMPLE)
        : note?.createdDateISO
          ? format(toZonedTime(note.createdDateISO, "UTC"), DATE_PATTERN_SIMPLE)
          : format(new Date(), DATE_PATTERN_SIMPLE),
      entity: {
        id: note?.entity._entityId ?? defaultNoteEntity?._entityId ?? "",
        type:
          note?.entity.type ??
          defaultNoteEntity?.type ??
          EnumEntityReferenceType.Person,
      },
    }),
    [note, defaultNoteEntity],
  );

  const { createNote, updateNote } = useNotesContext();

  return useFormHandler<NoteFormValues>(
    useCallback(
      async data => {
        if (!data || !data.message) return;

        // We need to manually check for the dirty state by trimming the message,
        // ensuring all people mentioned are still mentioned, and checking the date
        const message = data.message.trim();
        const mentions =
          (data.mentions
            // Filter out any mentions that have been removed from the message
            ?.filter(mention => mention?.id && message.includes(mention.id))
            .map(mention => mention?.id) as string[]) ?? [];
        const entity =
          data.entity && data.entity.id && data.entity.type
            ? {
                _entityId: data.entity.id,
                type: data.entity.type,
              }
            : undefined;

        const isDirty =
          message !== defaultValues.message ||
          mentions.length !== (defaultValues.mentions?.length || 0) ||
          entity?._entityId !== defaultValues.entity?.id ||
          // Note that we're using the data.noteDateISO value here because that value is the 10 character string
          // that would match the defaultValues.noteDateISO value
          data.noteDateISO !== defaultValues.noteDateISO;

        // If there are no changes, just call the success callback
        // (this resets the mode to view)
        if (!isDirty) {
          onSuccess();
          return;
        }

        const toSubmit = {
          message,
          mentions,
          noteDateISO: data.noteDateISO
            ? new Date(data.noteDateISO).toISOString()
            : undefined,
          entity,
        };

        let success = false;
        if (note) {
          success = await updateNote(note._id, toSubmit);
        } else {
          success = await createNote(toSubmit);
        }

        if (success) {
          onSuccess();
        } else {
          onError();
        }
      },
      [defaultValues, note, createNote, updateNote, onSuccess, onError],
    ),
    defaultValues,
    { schema: noteFormSchema, mode: "onBlur" },
  );
};
